import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { NotificationTypeEnum } from '@edtech-ui/constants/notification.enum';
import { IstDatePipe } from '@edtech-ui/core/models/Classroom/ist-date.pipe';
import { MarkNotificationPayload } from '@edtech-ui/core/models/notifications/marked-notification-response';
import { NotificationService } from '@edtech-ui/core/services/notification-managament-services/notification.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { DeleteDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/delete-dialog-box/delete-dialog-box.component';
import { NotificationItem } from '@edtech-ui/core/models/notifications/get-notification-response';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';

@Component({
	selector: 'app-student-notifications',
	standalone: true,
	imports: [CommonModule,
		MatIconModule, CommonModule, MatListModule, MatMenuTrigger, IstDatePipe, MatProgressSpinnerModule,
		MatMenuModule, MatButtonModule ,HasPermissionDirective
	],
	templateUrl: './student-notifications.component.html',
	styleUrl: './student-notifications.component.scss'
})
export class StudentNotificationsComponent implements OnInit, OnDestroy {

	notificationTypeEnum = NotificationTypeEnum;
	subscription = new Subscription();
	studentNotifications: NotificationItem[] = [];
	totalNotificationCount: number = 0;
	isLoading: boolean = true;
	isNotificationOpen = false;
	PermissionForStudents = PermissionForStudents;

	constructor(
		private _notificationService: NotificationService,
		private router: Router,
		private _toastr: ToastrService,
		private dialog: MatDialog,
	) {
		this.subscription.add(this._notificationService.reload$.pipe(tap(() => this.getNotifications())).subscribe());
	}


	ngOnInit(): void {
		this.getNotifications();
	}

	getNotifications(): void {
		this.isLoading = true;
		this.subscription.add(
			this._notificationService.getNotificationsForStudent().pipe(
				tap(response => {
					if (response.Data && response.Success) {
						this.studentNotifications = response.Data;
						const unreadNotifications = response.Data.filter((n: any) => !n.is_read);
						this.totalNotificationCount = unreadNotifications.length;
					}
				}),
				catchError(() => {
					this.isLoading = false;
					return of(null);
				}),
				finalize(() => { this.isLoading = false })
			).subscribe()
		);
	}

	putNotifications(notificationIds: string[]) {

		const unreadNotification = this.studentNotifications.filter(n => notificationIds.includes(n.id) && !n.is_read);
		if (!unreadNotification.length) return;

		const payload: MarkNotificationPayload = { notification_ids: unreadNotification.map(n => n.id) }

		this._notificationService.putMarkedNotifications(payload).pipe(
			tap(response => {
				if (response.Success) {
					unreadNotification.forEach(n => n.is_read = true);
				}
			}),
			catchError(err => of(null))
		).subscribe();
	}


	deleteNotification(notificationId: string) {
		this.subscription.add(
			this._notificationService.deleteNotification(notificationId).subscribe({
				next: () => {
					this._toastr.success('Notification deleted successfully');
					this.getNotifications();
				},
				error: (err) => {
					this._toastr.error('Failed to delete notification');
				}
			})
		);
	}


	openDeleteNotificationBox(notificationId: string) {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { notificationId }
		}).afterClosed().subscribe(result => {
			if (result) {
				this.deleteNotification(notificationId);
			}
		});
	}

	openNotifications() {
		this.isNotificationOpen = !this.isNotificationOpen;
	}

	markAllNotificationsAsRead() {
		const unreadIds = this.studentNotifications.filter(n => !n.is_read).map(n => n.id);

		if (!unreadIds.length) return;
		this.putNotifications(unreadIds);
		this.totalNotificationCount = 0;
	}


	onNotificationClick(notification: NotificationItem): void {
		if (!notification.is_read) {
			this.putNotifications([notification.id]);
			this.totalNotificationCount = Math.max(0, this.totalNotificationCount - 1);
		}

		switch (notification.content_type) {
			case this.notificationTypeEnum.Course:
				this.navigateToCourse(notification.content_id);
				break;

			case this.notificationTypeEnum.Quiz:
				if (notification.class_id && notification.course_id) {
					this.navigateToQuiz(notification.class_id.toString(), notification.course_id.toString());
				}
				break;

			case this.notificationTypeEnum.Mentor_session:
			case this.notificationTypeEnum.APPOINTMENT_REJECTED:
			case this.notificationTypeEnum.APPOINTMENT_ACCEPTED:
				const tab = this.getMentorSessionTab(notification);
    				this.navigateToMentorSession(tab);
				break;

			case this.notificationTypeEnum.SUBSCRIPTION_ACTIVATION:
			this.goToPaymentAndBillingPage();
			break;

			case this.notificationTypeEnum.Job:
				if (notification.content_id && notification.content_id !== '0') {
					this.router.navigate(['/student-dashboard/jobs-detail-page', notification.content_id]);
				} else {
					this.router.navigate(['/student-dashboard/jobs']);
				}
				break;

			case this.notificationTypeEnum.MockAssessment:
				this.router.navigate(['/student-dashboard/placement-prep']);
				break;

			case this.notificationTypeEnum.MockInterview:
				if (notification.content_id && notification.content_id !== '0') {
					const url = notification.content_id;
					if (url.includes('/interview.ai/')) {
						const relativePath = url.substring(url.indexOf('/interview.ai/'));
						window.open(relativePath, '_blank');
					} else {
						window.open(url, '_blank');
					}
				} else {
					window.open('/student-dashboard/ai-interview-dashboard', '_blank');
				}
				break;

			case this.notificationTypeEnum.JobApplication:
			case this.notificationTypeEnum.HiringComplete:
				this.router.navigate(['/student-dashboard/jobs/my-applications']);
				break;

			case this.notificationTypeEnum.Assessment:
			case this.notificationTypeEnum.Interview:
				if (notification.content_id && notification.content_id !== '0') {
					this.router.navigate(['/student-dashboard/jobs/applied', notification.content_id]);
				} else {
					this.router.navigate(['/student-dashboard/jobs/my-applications']);
				}
				break;
		}
	}

	navigateToCourse(content_id: string) {
		this.router.navigate(['/student-dashboard/course-details', content_id]);
	}

	navigateToQuiz(classroomId: string, courseId: string) {
		this.router.navigate(['/student-dashboard/course-detail-page', classroomId, courseId, 'quiz-list']);
	}

	navigateToMentorSession(tab: string) {
		this.router.navigate(['/student-dashboard/my-sessions'], { queryParams: { tab } });
	}

	goToPaymentAndBillingPage(){
		this.router.navigate(['/student-dashboard/payment-history']);
	}

	get unreadNotificationCount(): number {
		return this.studentNotifications?.filter(n => !n.is_read).length || 0;
	}

	getNotificationIcon(type: string): string {
		switch (type) {
			case 'quiz':
				return 'quiz';
			case 'course':
				return 'school';
			case 'mentor_session':
			case 'appointment_accepted':
			case 'appointment_rejected':
				return 'person';
			case 'subscription_activation':
				return 'payment';
			case this.notificationTypeEnum.Job:
				return 'work';
			default:
				return 'notifications';
		}
	}

	goToHome() {
		this.router.navigate(['/student-dashboard/home'])
	}

	getMentorSessionTab(notification: NotificationItem): string {
		switch (notification.content_type) {

			case this.notificationTypeEnum.APPOINTMENT_ACCEPTED:
				return 'upcoming';

			case this.notificationTypeEnum.APPOINTMENT_REJECTED:
				return 'cancelled';

			case this.notificationTypeEnum.Mentor_session:
				const title = notification.title?.toLowerCase() || '';

				if (title.includes('payment confirmed') || title.includes('payment successful')) {
					return 'confirmed';
				}

				if (title.includes('refund processed') || title.includes('cancelled')) {
					return 'cancelled';
				}

				if (title.includes('accepted')) {
					return 'upcoming'
				}

				return 'all';
			default:
				return 'all';
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}